import { isApiTrue } from "@/utils/apiBoolean";
import React, { useEffect, useState } from "react";
import {
  CButton,
  CCard,
  CCardBody,
  CCardGroup,
  CCol,
  CImage,
  CRow,
  CSpinner,
  CBadge,
  CNav,
  CNavItem,
  CNavLink,
  CTabContent,
  CTabPane,
} from "@coreui/react";
import { useGetAdminUserDetails, useUpdateUserStatus } from "@/hooks";
import UserActionsMenu from "@/components/UserActionsMenu";
import { showDeactivateDialog, showConfirmDialog } from "@/components/contentModeration/moderationDialogs";
import { contentModeration } from "@/constants/translation/contentModeration.translation";
import { userRestriction } from "@/constants/translation/userRestriction.translation";
import { useParams, useNavigate } from "react-router-dom";
import { userMaster } from "@/libs/routes";
import { useTranslationContext } from "@/context/TranslationContext";
import { titles } from "@/libs/constant";
import { breadcrumb_key } from "@/constants/translation/common.translation";
import userTranslations from "@/constants/translation/userMaster.translation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faUser, faEnvelope, faPhone, faCalendar, faMapMarkerAlt, faGraduationCap, faBriefcase, faHeart, faEye } from "@fortawesome/free-solid-svg-icons";
import avatar from "@/assets/images/avatars/DefaultImg.png";
import { setFormatDate, dateTimeFormat } from "@/utils/dateFormate";
import constants from "@/libs/constant";

const { status } = constants;
const user = userTranslations;

/**
 * @name ViewUser
 *
 * @description
 * The ViewUser component displays detailed user information in a read-only format.
 * It fetches user data based on the user_id from the URL and displays comprehensive
 * user profile information including personal details, profile information, education,
 * work experience, interests, and appearances.
 *
 * The component:
 * - Fetches user details using `useGetAdminUserDetails`.
 * - Displays user information in organized tabs.
 * - Shows profile photo, personal information, and extended profile data.
 * - Provides navigation back to user list and to edit page.
 *
 * @returns {JSX.Element} The JSX for rendering the user view page.
 */
const ViewUser = () => {
  const t = useTranslationContext();
  const navigate = useNavigate();
  const params = useParams();
  const user_id = params.id; // React Router uses 'id' for the :id parameter

  // State for active tab
  const [activeTab, setActiveTab] = useState(constants.userTabs.PERSONAL);

  // API call to get user details
  const {
    data: userDetails,
    isLoading: isLoadingUserDetails,
    error: userError,
    refetch,
  } = useGetAdminUserDetails(
    { uuid: user_id },
    null,
    (error) => {
      console.error('Error fetching user details:', error);
    }
  );

  const { mutate: doUpdateStatus } = useUpdateUserStatus(() => {
    refetch();
  });

  // Helper function to decrypt status values
  const decryptStatus = (encryptedValue) => {
    return constants.encryptedStatus.active.includes(encryptedValue) ? status.apiStatus.active : status.apiStatus.inactive;
  };

  // Handle loading state
  if (isLoadingUserDetails) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <CSpinner color="primary" />
      </div>
    );
  }

  // Handle error state
  if (userError || !userDetails?.data) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <h4>{t(user.view.error.user_not_found)}</h4>
          <p>{t(user.view.error.user_not_found_description)}</p>
          <CButton color="primary" onClick={() => navigate(userMaster.list)}>
            {t(user.view.error.back_to_user_list)}
          </CButton>
        </div>
      </div>
    );
  }

  const userData = userDetails.data;
  
  return (
    <>
      <div className="view-user-container">

        {/* Restriction details: reason, end date, who restricted and when */}
        {isApiTrue(userData.is_restricted) && (
          <CCard className="mb-4" style={{ borderColor: "var(--cui-warning)" }}>
            <CCardBody>
              <h5 className="mb-3">{t(userRestriction.restriction_card_title)}</h5>
              <CRow className="g-3">
                <CCol md={6}>
                  <strong>{t(userRestriction.restriction_reason)}:</strong> {userData.restriction_reason}
                </CCol>
                <CCol md={6}>
                  <strong>{t(userRestriction.restriction_end_date)}:</strong>{" "}
                  {userData.restriction_end_date
                    ? setFormatDate(userData.restriction_end_date)
                    : t(userRestriction.restriction_end_manual)}
                </CCol>
                <CCol md={6}>
                  <strong>{t(userRestriction.restriction_by)}:</strong> {userData.restricted_by || "-"}
                </CCol>
                <CCol md={6}>
                  <strong>{t(userRestriction.restriction_at)}:</strong>{" "}
                  {userData.restricted_at ? setFormatDate(userData.restricted_at) : "-"}
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        )}

        {/* Profile Header Card */}
        <CCard className="mb-4 profile-header-card">
          <CCardBody>
            <CRow className="align-items-center">
              <CCol md="auto" className="text-center mb-3 mb-md-0">
                <CImage
                  src={userData.profile_photo_url || avatar}
                  alt="profile_img"
                  className="rounded-circle"
                  style={{ 
                    width: '120px', 
                    height: '120px', 
                    objectFit: 'cover',
                    border: '4px solid #212631'
                  }}
                  onError={(e) => {
                    e.target.src = avatar;
                  }}
                />
              </CCol>
              <CCol>
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
                  <div>
                    <h2 className="mb-2">
                      {userData.full_name || userData.fullName || t(user.view.defaults.na)}
                      {userData.deleted_at && (
                        <CBadge color="danger" className="ms-2" style={{ fontSize: "0.75rem", padding: "0.35em 0.55em" }}>
                          {t(user.view.status_badges.deleted)}
                        </CBadge>
                      )}
                    </h2>
                    <div className="d-flex flex-wrap gap-3 mb-3">
                      <span className="text-muted">
                        <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                        {userData.email || t(user.view.defaults.na)}
                      </span>
                      <span className="text-muted">
                        <FontAwesomeIcon icon={faPhone} className="me-2" />
                        {userData.contact_number || t(user.view.defaults.na)}
                      </span>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      {isApiTrue(userData.is_restricted) && userData.admin_status === 'active' ? (
                        <CBadge color="warning">
                          {t(user.view.status_badges.admin)}: {t(userRestriction.status_restricted).toUpperCase()}
                        </CBadge>
                      ) : (
                        <CBadge color={userData.admin_status === 'active' ? 'success' : 'danger'}>
                          {t(user.view.status_badges.admin)}: {userData.admin_status ? userData.admin_status.toUpperCase() : t(user.view.defaults.na)}
                        </CBadge>
                      )}
                      <CBadge color={userData.is_active === 'true' ? 'success' : 'danger'}>
                         {t(user.view.status_badges.user)}: {userData.is_active === 'true' ? 'ACTIVE' : 'INACTIVE'}
                      </CBadge>
                      {isApiTrue(userData.is_restricted) && userData.admin_status !== 'active' && (
                        <CBadge color="warning">{t(userRestriction.status_restricted).toUpperCase()}</CBadge>
                      )}
                      <CBadge color="info">
                        {userData.signup_type ? userData.signup_type.toUpperCase() : t(user.view.defaults.normal_signup)}
                      </CBadge>
                    </div>
                  </div>
                </div>
              </CCol>
              <CCol md="auto" className="ms-md-auto mt-3 mt-md-0">
                <UserActionsMenu
                  uuid={userData.uuid}
                  isRestricted={isApiTrue(userData.is_restricted)}
                  isDeleted={Boolean(userData.deleted_at)}
                  deactivateLabel={
                    userData.admin_status === "active"
                      ? t(userRestriction.deactivate_action)
                      : t(userRestriction.activate_action)
                  }
                  onDeactivate={() =>
                    userData.admin_status === "active"
                      ? showDeactivateDialog({
                          t,
                          onConfirm: (reason) =>
                            doUpdateStatus({
                              id: userData.uuid,
                              type: "admin",
                              status: status.apiStatus.deactivated,
                              reason,
                            }),
                        })
                      : showConfirmDialog({
                          t,
                          texts: contentModeration.popups.reactivate,
                          onConfirm: () =>
                            doUpdateStatus({ id: userData.uuid, type: "admin", status: status.apiStatus.active }),
                        })
                  }
                  onChanged={refetch}
                />
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        {/* Navigation Tabs */}
        <CNav variant="tabs" className="mb-4 view-user-tabs">
          <CNavItem>
            <CNavLink 
              href="#" 
              active={activeTab === constants.userTabs.PERSONAL} 
              onClick={(e) => { e.preventDefault(); setActiveTab(constants.userTabs.PERSONAL); }}
            >
              <FontAwesomeIcon icon={faUser} className="me-2" />
              {t(user.view.tabs.personal_info)}
            </CNavLink>
          </CNavItem>
          <CNavItem>
            <CNavLink 
              href="#" 
              active={activeTab === constants.userTabs.PROFILE} 
              onClick={(e) => { e.preventDefault(); setActiveTab(constants.userTabs.PROFILE); }}
            >
              <FontAwesomeIcon icon={faEye} className="me-2" />
              {t(user.view.tabs.profile_details)}
            </CNavLink>
          </CNavItem>
          <CNavItem>
            <CNavLink 
              href="#" 
              active={activeTab === constants.userTabs.EDUCATION} 
              onClick={(e) => { e.preventDefault(); setActiveTab(constants.userTabs.EDUCATION); }}
            >
              <FontAwesomeIcon icon={faGraduationCap} className="me-2" />
              {t(user.view.tabs.education)}
            </CNavLink>
          </CNavItem>
          <CNavItem>
            <CNavLink 
              href="#" 
              active={activeTab === constants.userTabs.WORK} 
              onClick={(e) => { e.preventDefault(); setActiveTab(constants.userTabs.WORK); }}
            >
              <FontAwesomeIcon icon={faBriefcase} className="me-2" />
              {t(user.view.tabs.work_experience)}
            </CNavLink>
          </CNavItem>
          <CNavItem>
            <CNavLink 
              href="#" 
              active={activeTab === constants.userTabs.INTERESTS} 
              onClick={(e) => { e.preventDefault(); setActiveTab(constants.userTabs.INTERESTS); }}
            >
              <FontAwesomeIcon icon={faHeart} className="me-2" />
              {t(user.view.tabs.interests_appearances)}
            </CNavLink>
          </CNavItem>
        </CNav>

        {/* Tab Content */}
        <CTabContent>
          {/* Personal Information Tab */}
          <CTabPane visible={activeTab === constants.userTabs.PERSONAL}>
            <CCard>
              <CCardBody>
                <h4 className="card-title mb-4">{t(user.view.personal_info.title)}</h4>
                <CRow>
                  <CCol md={6} className="mb-3">
                    <strong>{t(user.view.personal_info.full_name)}:</strong>
                    <p className="text-muted">{userData.full_name || userData.fullName || t(user.view.defaults.na)}</p>
                  </CCol>
                  <CCol md={6} className="mb-3">
                    <strong>{t(user.view.personal_info.email)}:</strong>
                    <p className="text-muted">{userData.email || t(user.view.defaults.na)}</p>
                  </CCol>
                  <CCol md={6} className="mb-3">
                    <strong>{t(user.view.personal_info.contact_number)}:</strong>
                    <p className="text-muted">{userData.contact_number || t(user.view.defaults.na)}</p>
                  </CCol>
                  <CCol md={6} className="mb-3">
                    <strong>{t(user.view.personal_info.username)}:</strong>
                    <p className="text-muted">{userData.user_name || t(user.view.defaults.na)}</p>
                  </CCol>
                  <CCol md={6} className="mb-3">
                    <strong>{t(user.view.personal_info.admin_status)}:</strong>
                    <p>
                      <CBadge color={userData.admin_status === 'active' ? 'success' : 'danger'}>
                        {userData.admin_status ? userData.admin_status.toUpperCase() : t(user.view.defaults.na)}
                      </CBadge>
                    </p>
                  </CCol>
                  <CCol md={6} className="mb-3">
                    <strong>{t(user.view.personal_info.user_status)}:</strong>
                    <p>
                      <CBadge color={userData.is_active === 'true' ? 'success' : 'danger'}>
                        {userData.is_active === 'true' ? 'ACTIVE' : 'INACTIVE'}
                      </CBadge>
                    </p>
                  </CCol>
                  <CCol md={6} className="mb-3">
                    <strong>{t(user.view.personal_info.signup_type)}:</strong>
                    <p className="text-muted">{userData.signup_type ? userData.signup_type.toUpperCase() : t(user.view.defaults.normal_signup)}</p>
                  </CCol>
                  <CCol md={6} className="mb-3">
                    <strong>{t(user.view.personal_info.app_version)}:</strong>
                    <p className="text-muted">{userData.app_version || t(user.view.defaults.na)}</p>
                  </CCol>
                  <CCol md={6} className="mb-3">
                    <strong>{t(user.view.personal_info.email_verified)}:</strong>
                    <p>
                      <CBadge color={userData.is_email_verified === 'true' ? 'success' : 'secondary'}>
                        {userData.is_email_verified === 'true' ? 'ACTIVE' : 'INACTIVE'}
                      </CBadge>
                    </p>
                  </CCol>
                  <CCol md={6} className="mb-3">
                    <strong>{t(user.view.personal_info.phone_verified)}:</strong>
                    <p>
                      <CBadge color={userData.is_phone_verified === 'true' ? 'success' : 'secondary'}>
                        {userData.is_phone_verified === 'true' ? 'ACTIVE' : 'INACTIVE'}
                      </CBadge>
                    </p>
                  </CCol>
                  <CCol md={6} className="mb-3">
                    <strong>{t(user.view.personal_info.two_factor_sms_enabled)}:</strong>
                    <p>
                      <CBadge color={userData.two_factor_sms_enabled === 'true' ? 'success' : 'secondary'}>
                        {userData.two_factor_sms_enabled === 'true' ? 'ACTIVE' : 'INACTIVE'}
                      </CBadge>
                    </p>
                  </CCol>
                  <CCol md={6} className="mb-3">
                    <strong>{t(user.view.personal_info.two_factor_email_enabled)}:</strong>
                    <p>
                      <CBadge color={userData.two_factor_email_enabled === 'true' ? 'success' : 'secondary'}>
                        {userData.two_factor_email_enabled === 'true' ? 'ACTIVE' : 'INACTIVE'}
                      </CBadge>
                    </p>
                  </CCol>
                  <CCol md={6} className="mb-3">
                    <strong>{t(user.view.personal_info.joined_date)}:</strong>
                    <p className="text-muted">{setFormatDate(userData.created_at)}</p>
                  </CCol>
                  <CCol md={6} className="mb-3">
                    <strong>{t(user.view.personal_info.last_login)}:</strong>
                    <p className="text-muted">{dateTimeFormat(userData.last_login_at)}</p>
                  </CCol>
                  <CCol md={6} className="mb-3">
                    <strong>{t(user.view.personal_info.last_online)}:</strong>
                    <p className="text-muted">{dateTimeFormat(userData.last_online_at)}</p>
                  </CCol>
                </CRow>
              </CCardBody>
            </CCard>
          </CTabPane>

          {/* Profile Details Tab */}
          <CTabPane visible={activeTab === constants.userTabs.PROFILE}>
            <CCard>
              <CCardBody>
                <h4 className="card-title mb-4">{t(user.view.profile_details.title)}</h4>
                {userData.profile ? (
                  <>
                    <CRow>
                      <CCol md={6} className="mb-3">
                        <strong>{t(user.view.profile_details.bio)}:</strong>
                        <p className="text-muted">{userData.profile.bio || t(user.view.defaults.na)}</p>
                      </CCol>
                      <CCol md={6} className="mb-3">
                        <strong>{t(user.view.profile_details.about_me)}:</strong>
                        <p className="text-muted">{userData.profile.about_me || t(user.view.defaults.na)}</p>
                      </CCol>
                      <CCol md={6} className="mb-3">
                        <strong>{t(user.view.profile_details.date_of_birth)}:</strong>
                        <p className="text-muted">{userData.profile.date_of_birth || t(user.view.defaults.na)}</p>
                      </CCol>
                      <CCol md={6} className="mb-3">
                        <strong>{t(user.view.profile_details.gender)}:</strong>
                        <p className="text-muted">{userData.profile.gender || t(user.view.defaults.na)}</p>
                      </CCol>
                      <CCol md={6} className="mb-3">
                        <strong>{t(user.view.profile_details.house_number)}:</strong>
                        <p className="text-muted">{userData.profile.house_number || t(user.view.defaults.na)}</p>
                      </CCol>
                      <CCol md={6} className="mb-3">
                        <strong>{t(user.view.profile_details.street)}:</strong>
                        <p className="text-muted">{userData.profile.street || t(user.view.defaults.na)}</p>
                      </CCol>
                      <CCol md={6} className="mb-3">
                        <strong>{t(user.view.profile_details.zip_code)}:</strong>
                        <p className="text-muted">{userData.profile.zip_code || t(user.view.defaults.na)}</p>
                      </CCol>
                      <CCol md={6} className="mb-3">
                        <strong>{t(user.view.profile_details.city)}:</strong>
                        <p className="text-muted">{userData.profile.city || t(user.view.defaults.na)}</p>
                      </CCol>
                      <CCol md={6} className="mb-3">
                        <strong>{t(user.view.profile_details.country)}:</strong>
                        <p className="text-muted">{userData.profile.country || t(user.view.defaults.na)}</p>
                      </CCol>
                      <CCol md={6} className="mb-3">
                        <strong>{t(user.view.profile_details.preferred_language)}:</strong>
                        <p className="text-muted">{typeof userData.profile.preferred_language === 'object' ? userData.profile.preferred_language?.name || t(user.view.defaults.na) : userData.profile.preferred_language || t(user.view.defaults.na)}</p>
                      </CCol>
                      <CCol md={6} className="mb-3">
                        <strong>{t(user.view.profile_details.latitude)}:</strong>
                        <p className="text-muted">{userData.profile.latitude || t(user.view.defaults.na)}</p>
                      </CCol>
                      <CCol md={6} className="mb-3">
                        <strong>{t(user.view.profile_details.longitude)}:</strong>
                        <p className="text-muted">{userData.profile.longitude || t(user.view.defaults.na)}</p>
                      </CCol>
                    </CRow>
                    
                    {/* Cover Photo */}
                    {userData.profile.cover_photo_url && (
                      <CRow className="mt-4">
                        <CCol>
                          <strong>{t(user.view.profile_details.cover_photo)}:</strong>
                          <div className="mt-2">
                            <CImage
                              src={userData.profile.cover_photo_url}
                              alt="cover_photo"
                              style={{ 
                                maxWidth: '100%', 
                                maxHeight: '300px', 
                                objectFit: 'cover',
                                borderRadius: '8px'
                              }}
                            />
                          </div>
                        </CCol>
                      </CRow>
                    )}
                  </>
                ) : (
                  <p className="text-muted">{t(user.view.profile_details.no_profile_info)}</p>
                )}
              </CCardBody>
            </CCard>
          </CTabPane>

          {/* Education Tab */}
          <CTabPane visible={activeTab === constants.userTabs.EDUCATION}>
            <CCard>
              <CCardBody>
                <h4 className="card-title mb-4">{t(user.view.education.title)}</h4>
                {userData.profile?.education && userData.profile.education.length > 0 ? (
                  userData.profile.education.map((edu, index) => (
                    <CCard key={edu.uuid} className="mb-3">
                      <CCardBody>
                        <h6 className="card-subtitle mb-2 text-primary">
                          {typeof edu.degree === 'object' ? edu.degree?.name || t(user.view.defaults.na) : edu.degree || t(user.view.defaults.na)} in {typeof edu.field_of_study === 'object' ? edu.field_of_study?.name || t(user.view.defaults.na) : edu.field_of_study || t(user.view.defaults.na)}
                        </h6>
                        <p className="mb-1"><strong>{t(user.view.education.school)}:</strong> {edu.school || t(user.view.defaults.na)}</p>
                        <p className="mb-1"><strong>{t(user.view.education.graduation_year)}:</strong> {typeof edu.graduation_year === 'object' ? edu.graduation_year?.year || t(user.view.defaults.na) : edu.graduation_year || t(user.view.defaults.na)}</p>
                        <p className="mb-0">
                          <CBadge color={edu.is_primary === constants.encryptedStatus.active[0] ? 'success' : 'secondary'}>
                            {edu.is_primary === constants.encryptedStatus.active[0] ? t(user.view.education.primary) : t(user.view.education.secondary)}
                          </CBadge>
                        </p>
                      </CCardBody>
                    </CCard>
                  ))
                ) : (
                  <p className="text-muted">{t(user.view.education.no_education_info)}</p>
                )}
              </CCardBody>
            </CCard>
          </CTabPane>

          {/* Work Experience Tab */}
          <CTabPane visible={activeTab === constants.userTabs.WORK}>
            <CCard>
              <CCardBody>
                <h4 className="card-title mb-4">{t(user.view.work_experience.title)}</h4>
                {userData.profile?.jobs && userData.profile.jobs.length > 0 ? (
                  userData.profile.jobs.map((job, index) => (
                    <CCard key={job.uuid} className="mb-3">
                      <CCardBody>
                        <h6 className="card-subtitle mb-2 text-primary">{job.title || t(user.view.defaults.na)}</h6>
                        <p className="mb-1"><strong>{t(user.view.work_experience.company)}:</strong> {job.company || t(user.view.defaults.na)}</p>
                        <p className="mb-1"><strong>{t(user.view.work_experience.industry)}:</strong> {typeof job.industry === 'object' ? job.industry?.name || t(user.view.defaults.na) : job.industry || t(user.view.defaults.na)}</p>
                        <p className="mb-0"><strong>{t(user.view.work_experience.employment_type)}:</strong> {typeof job.employment_type === 'object' ? job.employment_type?.name || t(user.view.defaults.na) : job.employment_type || t(user.view.defaults.na)}</p>
                      </CCardBody>
                    </CCard>
                  ))
                ) : (
                  <p className="text-muted">{t(user.view.work_experience.no_work_info)}</p>
                )}
              </CCardBody>
            </CCard>
          </CTabPane>

          {/* Interests & Appearances Tab */}
          <CTabPane visible={activeTab === constants.userTabs.INTERESTS}>
            <CCardGroup>
              {/* Interests */}
              <CCard>
                <CCardBody>
                  <h4 className="card-title mb-4">{t(user.view.interests_appearances.interests_title)}</h4>
                  {userData.profile?.interests && userData.profile.interests.length > 0 ? (
                    <div className="d-flex flex-wrap gap-2">
                      {userData.profile.interests.map((interest) => (
                        <CBadge key={interest.uuid} color="info" className="p-2">
                          {typeof interest.name === 'object' ? interest.name?.name || t(user.view.defaults.na) : interest.name || t(user.view.defaults.na)}
                        </CBadge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted">{t(user.view.interests_appearances.no_interests)}</p>
                  )}
                </CCardBody>
              </CCard>

              {/* Appearances */}
              <CCard>
                <CCardBody>
                  <h4 className="card-title mb-4">{t(user.view.interests_appearances.appearances_title)}</h4>
                  {userData.profile?.appearances && userData.profile.appearances.length > 0 ? (
                    <div className="d-flex flex-wrap gap-2">
                      {userData.profile.appearances.map((appearance) => (
                        <CBadge key={appearance.uuid} color="secondary" className="p-2">
                          {typeof appearance.name === 'object' ? appearance.name?.name || t(user.view.defaults.na) : appearance.name || t(user.view.defaults.na)} ({typeof appearance.type === 'object' ? appearance.type?.name || t(user.view.defaults.na) : appearance.type || t(user.view.defaults.na)})
                        </CBadge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted">{t(user.view.interests_appearances.no_appearances)}</p>
                  )}
                </CCardBody>
              </CCard>
            </CCardGroup>
          </CTabPane>
        </CTabContent>
      </div>
    </>
  );
};

// Static breadcrumb
ViewUser.breadCrumbArray = [
  {
    name: breadcrumb_key.userMaster?.list,
    pathname: userMaster.list,
    active: false,
  },
  {
    name: breadcrumb_key.userMaster?.view,
    pathname: '',
    active: true,
  }
];

export default ViewUser;
